import classNames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import React from 'react'

import { Icons } from '@/components/icons'

interface CustomMessageProps {
    message: string
    type: 'success' | 'info' | 'warning' | 'error' | 'customColor'
    position?: 'top-right' | 'top-left'
    customColor?: string
    title?: string
    description?: string
    onClose?: () => void
    visible: boolean
    duration?: number
}

export const ToastMessage: React.FC<CustomMessageProps> = ({
    message,
    type,
    position = 'top-right',
    customColor,
    title,
    description,
    onClose,
    visible,
    duration = 5000
}): React.ReactElement => {
    React.useEffect(() => {
        if (visible && onClose) {
            const timer = setTimeout(() => {
                onClose()
            }, duration)

            return (): void => clearTimeout(timer)
        }
    }, [visible, onClose, duration])

    const messageStyles = classNames(
        'fixed p-4 rounded-lg shadow-lg flex items-center z-[9999]', // Add z-[9999] here
        {
            'bg-green-100 dark:bg-success-900/30 border-l-4 border-green-500':
                type === 'success',
            'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-blue-500':
                type === 'info',
            'bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500':
                type === 'warning',
            'bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500':
                type === 'error',
            'bg-gray-100 dark:bg-gray-900/30 border-l-4': type === 'customColor'
        }
    )

    const positionStyles =
        position === 'top-right' ? 'top-4 right-4' : 'top-4 left-4'

    const getIcon = (): React.ReactElement => {
        const iconClass = 'text-2xl mr-3 flex-shrink-0'

        switch (type) {
            case 'success':
                return (
                    <Icons.CheckCircle
                        className={`${iconClass} text-green-500`}
                    />
                )
            case 'info':
                return (
                    <Icons.InfoCircle
                        className={`${iconClass} text-blue-500`}
                    />
                )
            case 'warning':
                return (
                    <Icons.ExclamationTriangle
                        className={`${iconClass} text-yellow-500`}
                    />
                )
            default:
                return (
                    <Icons.Asterisk
                        className={`${iconClass} ${customColor ? `text-${customColor}` : 'text-gray-500'}`}
                    />
                )
        }
    }

    const textColorClass =
        type === 'customColor' && customColor
            ? `text-${customColor}`
            : 'text-gray-800'

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className={`${messageStyles} ${positionStyles} max-w-md w-full`}
                    style={{
                        borderLeftColor:
                            type === 'customColor' ? customColor : undefined
                    }}
                >
                    <div className="flex items-center mr-3">
                        <img
                            src="/LogoFavicon.webp"
                            alt="Universidad EAN Logo"
                            width={40}
                            height={40}
                            className="mr-2"
                        />
                        {getIcon()}
                    </div>
                    <div className="flex-grow mr-2">
                        {title && (
                            <h3
                                className={`font-semibold ${textColorClass} text-lg mb-1`}
                            >
                                {title}
                            </h3>
                        )}
                        {description && (
                            <h4
                                className={`text-sm opacity-90 ${textColorClass} mb-1`}
                            >
                                {description}
                            </h4>
                        )}
                        <p className={`${textColorClass} text-sm`}>{message}</p>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0"
                        >
                            <Icons.Times />
                        </button>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}
